<script lang="ts" generics="T">
	import Radio from './Radio.svelte'
	import { HighlightedText, Icon } from '@repo/commons/components'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	type ValueType = unknown | null

	type RadioOption = { value: ValueType; label: string; disabled?: boolean; tooltip?: TooltipProps }

	interface Props {
		name: string
		highlightPattern?: string
		options: RadioOption[]
		tooltip?: TooltipProps
		value: ValueType
		size?: 'medium' | 'small'
		legend?: string
		error?: string
		onchange: (selectedValue: ValueType) => void
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void
	}

	let {
		name = 'radio-group',
		options = [],
		tooltip,
		highlightPattern,
		value = null,
		legend = '',
		size = 'medium',
		onchange = () => {},
		onHighlightMatch = () => {},
		error = ''
	}: Props = $props()

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })

	const getTooltipProps = (optionTooltip: TooltipProps): TooltipProps => {
		return { ...DEFAULT_TOOLTIP_PROPS, ...optionTooltip }
	}

	function handleChange(selectedValue: ValueType) {
		value = selectedValue
		onchange(selectedValue)
	}
</script>

<div class="radio-group">
	{#if legend}
		<legend class="radio-group-legend">
			{#if highlightPattern}
				<HighlightedText text={legend} pattern={highlightPattern} {onHighlightMatch} />
			{:else}
				{legend}
			{/if}
			{#if tooltip?.text || tooltip?.html}
				{#await import('@repo/commons/components') then { Tooltip }}
					<Tooltip {...tooltipProps}>
						<Icon name="info" size="sm" class="text-brand" />
					</Tooltip>
				{/await}
			{/if}
		</legend>
	{/if}

	{#snippet radioOption(option: RadioOption)}
		<Radio
			{name}
			{highlightPattern}
			{size}
			value={String(option.value)}
			label={option.label}
			checked={value === option.value}
			disabled={option.disabled || false}
			onchange={() => handleChange(option.value)}
			{onHighlightMatch}
		/>
	{/snippet}

	{#each options as option}
		{#if option.tooltip?.text || option.tooltip?.html}
			<div class="radio-option">
				{@render radioOption(option)}
				{#await import('@repo/commons/components') then { Tooltip }}
					<Tooltip {...getTooltipProps(option.tooltip)}>
						<Icon name="info" size="sm" class="text-brand" />
					</Tooltip>
				{/await}
			</div>
		{:else}
			{@render radioOption(option)}
		{/if}
	{/each}

	{#if error}
		<div class="error-row">
			<div class="error-icon">
				<Icon name="error" size="md" variant="danger" />
			</div>
			<div class="error-message">{error}</div>
		</div>
	{/if}
</div>

<style>
	.radio-group {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.radio-option {
		@apply flex items-center gap-1;
	}

	.radio-group-legend {
		@apply text-base text-[#001626]/90 font-[400] leading-normal flex items-center gap-1;
	}

	.error-row {
		@apply self-stretch grow shrink basis-0 py-1 rounded justify-start items-center gap-2 inline-flex;
	}
	.error-icon {
		@apply w-6 h-6 flex-col justify-center items-center gap-2.5 inline-flex overflow-hidden;
	}
	.error-message {
		@apply w-[227.80px] text-[#e4485b] text-sm font-semibold leading-tight;
	}
</style>
